import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home as HomeIcon, Menu, X, Calendar, Trophy, Users, Phone, Info, ChevronRight, Globe, ChevronDown, ClipboardList, BarChart3, Megaphone, Camera } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [mobileSeasonOpen, setMobileSeasonOpen] = useState(false);
  const location = useLocation();
  const { isAdmin, logout } = useAuth();
  const { language, t, toggleLanguage } = useLanguage();
  const dropdownRef = useRef(null);

  // Close desktop dropdown when clicking outside (not for mobile)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setAboutOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset mobile submenu when main menu closes
  useEffect(() => {
    if (!isOpen) {
      setMobileAboutOpen(false);
      setMobileSeasonOpen(false);
    }
  }, [isOpen]);

  const aboutLinks = [
    { to: '/about', label: t('about') },
    { to: '/faq', label: language === 'gr' ? 'Συχνές Ερωτήσεις' : 'FAQ' },
    { to: '/privacy', label: t('privacyPolicy') },
    { to: '/terms', label: t('termsOfUse') },
    { to: '/cookies', label: language === 'gr' ? 'Πολιτική Cookies' : 'Cookie Policy' },
  ];

  const seasonLinks = [
    { to: '/scoreboard', label: t('scoreboard'), icon: Trophy },
    { to: '/schedule', label: t('schedule'), icon: Calendar },
    { to: '/results', label: t('results'), icon: ClipboardList },
    { to: '/rankings', label: t('rankings'), icon: BarChart3 },
  ];

  const season2Links = [
    { to: '/season2/scoreboard', label: t('scoreboard'), icon: Trophy },
    { to: '/season2/schedule', label: t('schedule'), icon: Calendar },
    { to: '/season2/results', label: t('results'), icon: ClipboardList },
    { to: '/season2/rankings', label: t('rankings'), icon: BarChart3 },
  ];

  return (
    <nav className="bg-white shadow-sm sticky top-0 z-50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20 items-center">
          <Link to="/" className="flex items-center space-x-3" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_deac57eb-5af3-4086-be87-3d979f3fa610/artifacts/wpheix3q_D5246629-C7A0-4803-86B6-1B81D2DFADD5.png" 
              alt="WBL Athens" 
              className="h-16 w-auto"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center space-x-5">
            <Link to="/" className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}>{t('home')}</Link>
            
            {/* About Dropdown - Hover based */}
            <div className="relative group">
              <button 
                className={`nav-link flex items-center gap-1 cursor-pointer ${['/about', '/privacy', '/terms', '/faq', '/cookies'].includes(location.pathname) ? 'active' : ''}`}
                type="button"
              >
                {t('aboutLeague')} <ChevronDown size={16} className="transition-transform duration-200 group-hover:rotate-180" />
              </button>
              <div className="absolute top-full left-0 mt-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="w-56 bg-white rounded-lg shadow-xl border border-slate-200 py-2">
                  {aboutLinks.map(link => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className="block px-4 py-2.5 text-slate-600 hover:bg-slate-50 hover:text-[#1E3A5F] transition"
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Season Dropdown - Hover based with nested submenu */}
            <div className="relative group">
              <button 
                className={`nav-link flex items-center gap-1 cursor-pointer ${['/schedule', '/scoreboard', '/results'].includes(location.pathname) ? 'active' : ''}`}
                type="button"
              >
                {language === 'gr' ? 'Σεζόν' : 'Season'} <ChevronDown size={16} className="transition-transform duration-200 group-hover:rotate-180" />
              </button>
              <div className="absolute top-full left-0 mt-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="w-48 bg-white rounded-lg shadow-xl border border-slate-200 py-2">
                  {/* Season 1 with nested menu */}
                  <div className="relative group/season1">
                    <div className="flex items-center justify-between px-4 py-2.5 text-slate-600 hover:bg-slate-50 hover:text-[#1E3A5F] cursor-pointer transition">
                      <span>{language === 'gr' ? 'Σεζόν 1' : 'Season 1'}</span>
                      <ChevronRight size={16} />
                    </div>
                    <div className="absolute left-full top-0 ml-0 pl-1 opacity-0 invisible group-hover/season1:opacity-100 group-hover/season1:visible transition-all duration-200">
                      <div className="w-48 bg-white rounded-lg shadow-xl border border-slate-200 py-2">
                        {seasonLinks.map(link => {
                          const Icon = link.icon;
                          return (
                            <Link key={link.to} to={link.to} className="flex items-center gap-2 px-4 py-2.5 text-slate-600 hover:bg-slate-50 hover:text-[#1E3A5F] transition"><Icon size={16} />{link.label}</Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  {/* Season 2 with nested menu */}
                  <div className="relative group/season2">
                    <div className="flex items-center justify-between px-4 py-2.5 text-slate-600 hover:bg-slate-50 hover:text-[#1E3A5F] cursor-pointer transition">
                      <span>{language === 'gr' ? 'Σεζόν 2' : 'Season 2'}</span>
                      <ChevronRight size={16} />
                    </div>
                    <div className="absolute left-full top-0 ml-0 pl-1 opacity-0 invisible group-hover/season2:opacity-100 group-hover/season2:visible transition-all duration-200">
                      <div className="w-48 bg-white rounded-lg shadow-xl border border-slate-200 py-2">
                        {season2Links.map(link => {
                          const Icon = link.icon;
                          return (
                            <Link key={link.to} to={link.to} className="flex items-center gap-2 px-4 py-2.5 text-slate-600 hover:bg-slate-50 hover:text-[#1E3A5F] transition"><Icon size={16} />{link.label}</Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <Link to="/teams" className={`nav-link ${location.pathname === '/teams' ? 'active' : ''}`}>{t('teams')}</Link>
            <Link to="/news" className={`nav-link ${location.pathname === '/news' ? 'active' : ''}`}>{t('news')}</Link>
            <Link to="/media" className={`nav-link ${location.pathname === '/media' ? 'active' : ''}`}>{t('media')}</Link>
            <Link to="/contact" className={`nav-link ${location.pathname === '/contact' ? 'active' : ''}`}>{t('contact')}</Link>
            
            <button onClick={toggleLanguage} className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 transition text-sm font-medium" data-testid="language-toggle">
              <Globe size={16} />
              {language === 'en' ? 'GR' : 'EN'}
            </button>
            
            {isAdmin ? (
              <>
                <Link to="/admin" className="btn-accent text-sm" data-testid="nav-admin">{t('adminPanel')}</Link>
                <button onClick={logout} className="text-slate-500 hover:text-slate-700" data-testid="logout-btn">{t('logout')}</button>
              </>
            ) : (
              <Link to="/login" className="btn-primary text-sm" data-testid="nav-login">{t('adminLogin')}</Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-2">
            <button onClick={toggleLanguage} className="flex items-center gap-1 px-2 py-1 rounded bg-slate-100 text-sm font-medium">
              {language === 'en' ? 'GR' : 'EN'}
            </button>
            <button className="p-2" onClick={() => setIsOpen(!isOpen)} data-testid="mobile-menu-btn">
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      {isOpen && (
        <div className="lg:hidden bg-white border-t" data-testid="mobile-menu">
          <div className="px-4 py-3 space-y-2">
            <Link to="/" onClick={() => setIsOpen(false)} className={`flex items-center space-x-3 p-3 rounded-lg ${location.pathname === '/' ? 'bg-slate-100 text-[#1E3A5F]' : 'text-slate-600'}`}>
              <HomeIcon size={20} /><span>{t('home')}</span>
            </Link>
            
            {/* About Section - Collapsible */}
            <div>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setMobileAboutOpen(!mobileAboutOpen);
                }}
                className={`flex items-center justify-between w-full p-3 rounded-lg ${['/about', '/privacy', '/terms', '/faq', '/cookies'].includes(location.pathname) ? 'bg-slate-100 text-[#1E3A5F]' : 'text-slate-600'}`}
              >
                <div className="flex items-center space-x-3">
                  <Info size={20} />
                  <span>{t('aboutLeague')}</span>
                </div>
                <ChevronDown size={20} className={`transition-transform duration-200 ${mobileAboutOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileAboutOpen && (
                <div className="border-l-2 border-[#F97316] ml-6 pl-3 mt-1 space-y-1">
                  {aboutLinks.map(link => (
                    <Link 
                      key={link.to} 
                      to={link.to} 
                      onClick={() => { setIsOpen(false); setMobileAboutOpen(false); }} 
                      className={`block p-2 rounded-lg text-sm ${location.pathname === link.to ? 'bg-slate-100 text-[#1E3A5F]' : 'text-slate-600'}`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            
            {/* Season Section - Collapsible */}
            <div>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setMobileSeasonOpen(!mobileSeasonOpen);
                }}
                className={`flex items-center justify-between w-full p-3 rounded-lg ${['/schedule', '/scoreboard', '/results'].includes(location.pathname) ? 'bg-slate-100 text-[#1E3A5F]' : 'text-slate-600'}`}
              >
                <div className="flex items-center space-x-3">
                  <Trophy size={20} />
                  <span>{language === 'gr' ? 'Σεζόν' : 'Season'}</span>
                </div>
                <ChevronDown size={20} className={`transition-transform duration-200 ${mobileSeasonOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileSeasonOpen && (
                <div className="border-l-2 border-[#F97316] ml-6 pl-3 mt-1 space-y-1">
                  <p className="text-xs font-bold text-[#1E3A5F] px-2 py-1">{language === 'gr' ? 'Σεζόν 1' : 'Season 1'}</p>
                  {seasonLinks.map(link => {
                    const Icon = link.icon;
                    return (
                      <Link 
                        key={link.to} 
                        to={link.to} 
                        onClick={() => { setIsOpen(false); setMobileSeasonOpen(false); }} 
                        className={`flex items-center gap-2 p-2 rounded-lg text-sm ${location.pathname === link.to ? 'bg-slate-100 text-[#1E3A5F]' : 'text-slate-600'}`}
                      >
                        <Icon size={16} />
                        {link.label}
                      </Link>
                    );
                  })}
                  <p className="text-xs font-bold text-[#1E3A5F] px-2 py-1 mt-2">{language === 'gr' ? 'Σεζόν 2' : 'Season 2'}</p>
                  {season2Links.map(link => {
                    const Icon = link.icon;
                    return (
                      <Link 
                        key={link.to} 
                        to={link.to} 
                        onClick={() => { setIsOpen(false); setMobileSeasonOpen(false); }} 
                        className={`flex items-center gap-2 p-2 rounded-lg text-sm ${location.pathname === link.to ? 'bg-slate-100 text-[#1E3A5F]' : 'text-slate-600'}`}
                      >
                        <Icon size={16} />
                        {link.label}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
            
            <Link to="/teams" onClick={() => setIsOpen(false)} className={`flex items-center space-x-3 p-3 rounded-lg ${location.pathname === '/teams' ? 'bg-slate-100 text-[#1E3A5F]' : 'text-slate-600'}`}>
              <Users size={20} /><span>{t('teams')}</span>
            </Link>
            <Link to="/news" onClick={() => setIsOpen(false)} className={`flex items-center space-x-3 p-3 rounded-lg ${location.pathname === '/news' ? 'bg-slate-100 text-[#1E3A5F]' : 'text-slate-600'}`}>
              <Megaphone size={20} /><span>{t('news')}</span>
            </Link>
            <Link to="/media" onClick={() => setIsOpen(false)} className={`flex items-center space-x-3 p-3 rounded-lg ${location.pathname === '/media' ? 'bg-slate-100 text-[#1E3A5F]' : 'text-slate-600'}`}>
              <Camera size={20} /><span>{t('media')}</span>
            </Link>
            <Link to="/contact" onClick={() => setIsOpen(false)} className={`flex items-center space-x-3 p-3 rounded-lg ${location.pathname === '/contact' ? 'bg-slate-100 text-[#1E3A5F]' : 'text-slate-600'}`}>
              <Phone size={20} /><span>{t('contact')}</span>
            </Link>
            
            {isAdmin ? (
              <Link to="/admin" onClick={() => setIsOpen(false)} className="block btn-accent text-center mt-2">{t('adminPanel')}</Link>
            ) : (
              <Link to="/login" onClick={() => setIsOpen(false)} className="block btn-primary text-center mt-2">{t('adminLogin')}</Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
